import type { ComponentType } from 'react';
import { scalePct } from 'src/lib/scoring.ts';
import { ResultType, Section } from 'src/types/types.ts';

import { PyramidSvg } from './pyramid-svg.tsx';
import { ScoreRing } from './score-ring.tsx';

/** `score: undefined` = keine substanziellen Antworten („keine Angabe"). */
export type Level = {
  id: Section['id'];
  name: string;
  score: number | undefined;
  tone: ResultType['tone'];
  Icon: ComponentType<{ className?: string }>;
  breakdown?: { id: string; label: string; score: number | undefined }[];
};

// Desktop geometry (lg+). CONNECTOR_W = gap(32px) + half pyramid(140px) = 172px from
// the card's inner edge to the pyramid's horizontal center.
const PYRAMID_W = 280;
const PYRAMID_H = Math.round((PYRAMID_W * 162) / 156); // SVG viewBox 156×162
const CARD_W = 300;
const CONNECTOR_W = 172;

function LevelBreakdown({ items }: { items: NonNullable<Level['breakdown']> }) {
  return (
    <div className="mt-3 border-t border-zeb-muted pt-3">
      {items.map((item) => (
        <div key={item.id} className="flex items-center gap-2.5 py-1">
          <span className="w-[140px] shrink-0 truncate text-meta text-zeb-gray-500">
            {item.label}
          </span>
          <span className="h-1 flex-1 overflow-hidden rounded-full bg-zeb-muted">
            {/* Gleiche 1–5-Verankerung wie die Fragen-Balken (scalePct), damit
                identische Werte auf derselben Seite gleich weit gefüllt sind. */}
            <span
              className="block h-full rounded-full bg-zeb-gray-300"
              style={{ width: `${item.score !== undefined ? scalePct(item.score) : 0}%` }}
            />
          </span>
          <span className="w-8 shrink-0 text-right text-meta tabular-nums text-zeb-gray-500">
            {item.score !== undefined ? item.score.toFixed(1).replace('.', ',') : '–'}
          </span>
        </div>
      ))}
    </div>
  );
}

/** Single level card: compact score ring (icon centered) + name and score.
 * When `level.breakdown` is present (Wirkungsebenen), a muted sub-score list
 * is appended below a divider — deliberately smaller/quieter than the main
 * score so the 3 pyramid results stay the visual focus. */
function LevelCard({ level }: { level: Level }) {
  return (
    <div className="flex w-full flex-col rounded-lg border border-zeb-muted bg-white px-5 py-4 shadow-zeb-xs">
      <div className="flex items-center gap-4">
        <ScoreRing value={level.score ?? 0} tone={level.tone} Icon={level.Icon} />
        <div className="min-w-0">
          <div
            className="text-body-lg tracking-tight text-zeb-tertiary"
            style={{
              fontFamily: 'var(--font-heading)',
              fontWeight: 700,
              letterSpacing: '-0.02em'
            }}>
            {level.name}
          </div>
          {level.score !== undefined ? (
            <div className="mt-0.5 flex items-baseline gap-1.5">
              <span
                className="text-2xl tabular-nums leading-none text-zeb-tertiary"
                style={{
                  fontFamily: 'var(--font-heading)',
                  fontWeight: 700,
                  letterSpacing: '-0.02em'
                }}>
                {level.score.toFixed(1).replace('.', ',')}
              </span>
              <span className="text-xs text-zeb-gray-500">/ 5,0</span>
            </div>
          ) : (
            <div className="mt-0.5 text-sm text-zeb-gray-500">keine Angabe</div>
          )}
        </div>
      </div>
      {level.breakdown && <LevelBreakdown items={level.breakdown} />}
    </div>
  );
}

/** Connector from a card's inner edge to the pyramid center, with a dot at the end. */
function Connector({ goLeft }: { goLeft: boolean }) {
  return (
    <div
      className="pointer-events-none absolute top-1/2 -translate-y-1/2"
      style={{ width: CONNECTOR_W, height: 2, [goLeft ? 'right' : 'left']: '100%' }}>
      <div className="h-full w-full bg-zeb-primary" />
      <div
        className="absolute top-1/2 h-3 w-3 rounded-full bg-zeb-primary ring-4 ring-zeb-primary/20"
        style={
          goLeft
            ? { left: 0, transform: 'translate(-50%, -50%)' }
            : { right: 0, transform: 'translate(50%, -50%)' }
        }
      />
    </div>
  );
}

// tier = fraction of PYRAMID_H where the connector dot lands (visual center of each SVG layer).
// goLeft = connector points left → card sits on the right side of the pyramid.
const TIERS = [
  { tier: 0.21, goLeft: false }, // Strategie -> left
  { tier: 0.55, goLeft: true }, // Loop -> right
  { tier: 0.86, goLeft: false } // Wirkungsebenen -> left (taller: has the breakdown block)
] as const;

/**
 * Result section: the zeb pyramid framework with per-level score cards.
 * Desktop (lg+): pyramid centered, cards alternating right/left aligned to its
 * tiers, each linked to the pyramid center by a connector line + dot.
 * Below lg: simple stack — pyramid on top, full-width cards below.
 */
export function LevelScores({ levels }: { levels: Level[] }) {
  return (
    <>
      {/* Desktop/tablet: pyramid centered, cards absolutely positioned at tier midpoints */}
      <div className="relative hidden lg:block" style={{ height: PYRAMID_H }}>
        <div className="absolute left-1/2 top-0 -translate-x-1/2">
          <PyramidSvg activeIndex={null} width={PYRAMID_W} />
        </div>
        {levels.map((level, i) => {
          const { tier, goLeft } = TIERS[i];
          const pos = goLeft
            ? { left: `calc(50% + ${CONNECTOR_W}px)` }
            : { right: `calc(50% + ${CONNECTOR_W}px)` };
          return (
            <div
              key={level.id}
              className="absolute -translate-y-1/2"
              style={{ width: CARD_W, top: Math.round(tier * PYRAMID_H), ...pos }}>
              <LevelCard level={level} />
              <Connector goLeft={goLeft} />
            </div>
          );
        })}
      </div>

      {/* Mobile (< lg): stacked, full-width cards */}
      <div className="lg:hidden flex flex-col gap-5">
        <div className="mx-auto w-full max-w-[260px]">
          <PyramidSvg activeIndex={null} width="100%" />
        </div>
        {levels.map((level) => (
          <LevelCard key={level.id} level={level} />
        ))}
      </div>
    </>
  );
}
